/**
 * 
 * @param {*} data 
 */
export const getCSVLineData = data => data.map(line => line.data);

/**
 * 
 * @param {*} data 
 */
export const formatLineDataDate = (data) => data.map(line => (
  {
    ...line,
    date: line.date.split(' ')[0],
  }
));

/**
 * Array of unique workouts completed.
 */
export const getUniqueWorkouts = (data) => {
  return [...new Set(data.map((item) => item.workout))];
};

/**
 * Array of unique exercises used in workouts.
 */
export const getUniqueExercises = (data) => {
  return [...new Set(data.map((item) => item.exercise))]
};

/**
 * Group array of objects by keys.
 */
export const groupByMultiple = (data, keys) => {
  return data.reduce((storage, item) => {
    const groupValues = keys.reduce((values, key) => {
      values[key] = item[key];
      return values;
    }, {});

    const group = Object.values(groupValues).join(' ');

    storage[group] = storage[group] || [];

    if (keys.every((key) => item[key] === groupValues[key])) {
      storage[group].push(item);
    }

    return storage;
  }, {});
};

export const groupBySingle = (data, key) => {
  return data.reduce(function (storage, item) {
    const group = item[key];
    storage[group] = storage[group] || [];
    storage[group].push(item);
    return storage;
  }, {});
};

/**
 * Convert object to an array.
 */
export const toArray = (obj) => Object.keys(obj).map((key) => obj[key]);

/**
 * 
 * @param {*} obj 
 * @param {*} propsToDelete 
 */
export const deleteSelectedObjProps = (obj, propsToDelete) => {
  for (let key in obj) {
    if (propsToDelete.includes(key)) {
      delete obj[key];
    }
  }
}

/**
 * The max-weight lifted for this exercise.
 */
export const maxWeightLifted = (arr) => {
  const max = arr.sort((a, b) => b.weight.kg - a.weight.kg)[0];

  return {
    kg: max.weight.kg,
    lb: max.weight.lb,
    reps: max.reps,
  }
};

/**
 * The highest one rep max calculated for this exercise.
 */
export const max1RM = (arr) => {
  const max = arr.sort((a, b) => b.oneRepMax.kg - a.oneRepMax.kg)[0];

  return {
    kg: max.oneRepMax.kg,
    lb: max.oneRepMax.lb,
  }
};

/**
 * Modify shape of data.
 */
export const modifyWorkoutObjectDataShape = (data) => {
  /**
   * Setup array to push in new workout object.
   */
  const heavysetData = [];

  /**
   * Clone the data array parameter to stop it modifying existing data.
   */
  const cloneData = [...data];

  /**
   * Loop over each workout in array.
   */
  cloneData.forEach(item => {
    /**
     * Only need the date and workout name for this workout once.
     */
    const date = item[0].date;
    const workout = item[0].workout;

    /**
     * Group the workout items in this array by 'exercise' e.g.
     * Bench Press: (5) [{…}, {…}, {…}, {…}, {…}]
     * We need to clone the object as we need to use two sets of 
     * data for different object shapes.
     */
    const exercises = groupBySingle(item, 'exercise');
    const clonedExercisesForSets = Object.assign({}, exercises);

    /**
     * We can delete these object properties, as we don't use them.
     */
    const deleteObjProps = [
      'date',
      'workout',
      'exercise',
      'notes',
      'duration',
      'weight (kg)',
      'weight (lb)',
    ];

    for (let key in clonedExercisesForSets) {
      clonedExercisesForSets[key].forEach((exercise) => {
        exercise.reps = Number(exercise.reps);
        
        exercise.weight = {
          kg: Number(exercise['weight (kg)']),
          lb: Number(Math.round(exercise['weight (lb)'])),
        };

        exercise.setVolume = {
          kg: exercise.reps * exercise['weight (kg)'],
          lb: exercise.reps * Math.round(exercise['weight (lb)']),
        };

        exercise.oneRepMax = {
          kg: Math.round(exercise.weight.kg * (1 + 0.0333 * exercise.reps)),
          lb: Math.round(exercise.weight.lb * (1 + 0.0333 * exercise.reps)),
        }

        deleteSelectedObjProps(exercise, deleteObjProps);
      });

      /**
       * Construct final object shape
       */
      const exerciseSets = [...clonedExercisesForSets[key]];

      // Total volume of weight lifted for all completed sets / reps.
      const totalVolume = (weightKey) =>
      exerciseSets.reduce((acc, val) => acc + val.setVolume[weightKey], 0);

      exercises[key] = {
        volume: {
          kg: totalVolume('kg'),
          lb: totalVolume('lb'),
        },
        maxWeight: maxWeightLifted(exerciseSets),
        max1RM: max1RM(exerciseSets),
        sets: [...clonedExercisesForSets[key]],
      };
    }

    /**
     * Create our final Workout data shape.
     */
    const heavysetDataItem = {
      date,
      workout,
      exercises: {
        ...exercises,
      },
    };

    heavysetData.push(heavysetDataItem);
  });

  return heavysetData;
}

export const getHeavysetData = (csvData) => {
  const a = formatLineDataDate(csvData);
  const b = groupByMultiple(a, ['date', 'workout']);
  const c = toArray(b);
  const d = modifyWorkoutObjectDataShape(c);
  return d;
}

/** 
 * Set the Personal Record weight lifted for this exercise.
 */
export const setExercisePR = (arr, exercise) => {
  const prWithWeight = arr.sort((a, b) => b.exercises[exercise].maxWeight.kg - a.exercises[exercise].maxWeight.kg)[0];

  const prWithNoWeight = arr.sort((a, b) => b.exercises[exercise].maxWeight.reps - a.exercises[exercise].maxWeight.reps)[0];

  if (prWithWeight.exercises[exercise].maxWeight.kg !== 0) {
    return {
      kg: prWithWeight.exercises[exercise].maxWeight.kg,
      lb: prWithWeight.exercises[exercise].maxWeight.lb,
      reps: prWithWeight.exercises[exercise].maxWeight.reps,
      date: prWithWeight.date,
    }
  } else {
    return {
      kg: prWithNoWeight.exercises[exercise].maxWeight.kg,
      lb: prWithNoWeight.exercises[exercise].maxWeight.lb,
      reps: prWithNoWeight.exercises[exercise].maxWeight.reps,
      date: prWithNoWeight.date,
    }
  }
}

/** 
 * Set the One Rep Max Personal Record for this exercise.
 */
export const setExercise1RM = (arr, exercise) => {
  const pr = arr.sort((a, b) => b.exercises[exercise].max1RM.kg - a.exercises[exercise].max1RM.kg)[0];

  return {
    kg: pr.exercises[exercise].max1RM.kg,
    lb: pr.exercises[exercise].max1RM.lb,
  }
}

/** 
 * The total number of reps completed for this exercise.
 */
export const setExerciseTotalReps = (arr, exercise) => {
  return arr.reduce((total, workout) => {
    const sum = workout.exercises[exercise].sets.reduce((total, reps) => total + reps.reps, 0);
    return total + sum;
  }, 0);
}

/** 
 * The total number of reps completed for this workout.
 */
export const setWorkoutTotalReps = (arr) => {
  const workoutExercises = arr.map(w => Object.values(w.exercises));
  const workoutReps =  workoutExercises.map(w => w.reduce((total, workout) => {
    const sum = workout.sets.reduce((total, reps) => total + reps.reps, 0);
    return total + sum;
  }, 0));

  return workoutReps.reduce((total, workout) => total + workout, 0);
}

/** 
 * The total volume lifted completed for this exercise.
 */
export const setExerciseTotalVolume = (arr, exercise) => (
  {
    kg: arr.reduce((total, workout) => total + workout.exercises[exercise].volume.kg, 0),
    lb: arr.reduce((total, workout) => total + workout.exercises[exercise].volume.lb, 0),
  }
);

/** 
 * Filter data by exercise
 */
export const filterByExercise = (arr, exercise) => (
  arr.filter(e => Object.keys(e.exercises).includes(exercise))
);

/**
 * Slugify string
 */
function slugify(string) {
  const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;';
  const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------';
  const p = new RegExp(a.split('').join('|'), 'g');

  return string.toString().toLowerCase()
    .replace(/\s+/g, '-')
    .replace(p, c => b.charAt(a.indexOf(c)))
    .replace(/&/g, '-and-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

export const urlSlug = (title) => `${slugify(title)}`;
